import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Empty, Flex, Skeleton, Tooltip, Typography } from "antd";
import { useState } from "react";
import {
  deleteZaloGroupMessage,
  getSupportedGroupEvents,
  getZaloGroupMessages,
} from "../../../api/zaloGroupMessage/zaloGroupMessage";
import { useAction, useAsync } from "../../../lib/useAsync";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import CmsTableCard from "../../../components/cms/CmsTableCard";
import ListToolbar from "../../../components/cms/ListToolbar";
import RowActions from "../../../components/cms/RowActions";
import StatusTag from "../../../components/cms/StatusTag";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import ZaloGroupMessageForm from "./ZaloGroupMessageForm";
import { neutral, typography } from "../../../theme/tokens";

const { Text, Paragraph } = Typography;

/**
 * Cấu hình tin gửi vào nhóm Zalo (GMF): mỗi sự kiện một dòng, có mã nhóm + brandname riêng
 * nên gửi được vào các nhóm khác nhau.
 *
 * Không có dải chỉ số: danh sách chỉ vài dòng, thêm khối đếm vào chỉ chiếm chỗ mà không cho
 * biết gì thêm.
 */
export default function ZaloGroupMessageTable() {
  const list = useAsync((signal) => getZaloGroupMessages(signal), []);
  const events = useAsync((signal) => getSupportedGroupEvents(signal), []);

  // null = đóng; {} = thêm mới; record = sửa.
  const [editing, setEditing] = useState(null);

  const remove = useAction(deleteZaloGroupMessage, {
    successMessage: "Đã xóa cấu hình tin nhóm",
    onSuccess: () => list.reload(),
  });

  const rows = (list.data?.data ?? []).map((item) => ({ ...item, key: item.id }));
  const eventOptions = events.data?.data ?? [];

  /*
   * Cấu hình tạo trên CMS luôn áp cho toàn bộ đơn vị, nên cột "Phạm vi" chỉ hiện khi dữ liệu
   * THỰC SỰ có dòng riêng theo đơn vị (backend vẫn hỗ trợ) — một cột mà mọi dòng cùng giá trị
   * thì chỉ chiếm chỗ.
   */
  const hasBranchScoped = rows.some((r) => Number(r.branch_id) !== 0);

  /*
   * Mỗi sự kiện chỉ MỘT cấu hình (unique code+branch_id ở BE). Biết trước sự kiện nào đã dùng
   * thì chặn được ngay ở ô chọn và ở nút Thêm mới, thay vì để người dùng điền xong mới báo lỗi.
   */
  const usedCodes = rows.map((r) => r.code);
  const canAddMore = eventOptions.some((e) => !usedCodes.includes(e.code));

  const columns = [
    {
      title: "Sự kiện",
      dataIndex: "name",
      width: 220,
      render: (text, record) => (
        <Flex vertical gap={2}>
          <Text strong>{text}</Text>
          <Text code style={{ fontSize: typography.size.xs, color: neutral[500] }}>
            {record.code}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Nội dung tin",
      dataIndex: "content",
      render: (text) => (
        // Giữ xuống dòng đúng như tin thật sẽ gửi vào nhóm, cắt 3 dòng cho bảng khỏi giãn.
        <Paragraph
          style={{ margin: 0, whiteSpace: "pre-line" }}
          ellipsis={{ rows: 3, expandable: true, symbol: "xem thêm" }}
        >
          {text}
        </Paragraph>
      ),
    },
    {
      title: "Nhóm nhận tin",
      dataIndex: "group_id",
      width: 220,
      render: (groupId, record) => (
        <Flex vertical gap={2}>
          <Text code style={{ fontSize: typography.size.xs }}>
            {groupId || "—"}
          </Text>
          <Text style={{ fontSize: typography.size.xs, color: neutral[500] }}>
            {record.brandname || "Chưa có brandname"}
          </Text>
        </Flex>
      ),
    },
    ...(hasBranchScoped
      ? [
          {
            title: "Phạm vi",
            dataIndex: "branch_id",
            width: 140,
            align: "center",
            // Đây là LOẠI, không phải trạng thái -> tag xám theo luật màu của StatusTag.
            render: (branchId) => (
              <StatusTag>
                {Number(branchId) === 0 ? "Dùng chung" : `Đơn vị #${branchId}`}
              </StatusTag>
            ),
          },
        ]
      : []),
    {
      title: "Trạng thái",
      dataIndex: "is_active",
      width: 130,
      align: "center",
      render: (isActive) => (
        <StatusTag tone={isActive ? "success" : "danger"} dot>
          {isActive ? "Đang gửi" : "Đã tắt"}
        </StatusTag>
      ),
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Flex align="center" justify="center">
          <RowActions
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Sửa cấu hình tin",
                onClick: () => setEditing(record),
              },
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa cấu hình tin",
                danger: true,
                divider: true,
                onClick: () =>
                  confirmDelete(
                    record.name || `cấu hình #${record.id}`,
                    () => remove.run(record.id),
                    "Xóa rồi thì sự kiện này ngừng gửi tin vào nhóm. Chỉ muốn tạm dừng thì sửa và tắt \"Bật gửi tin\"."
                  ),
              },
            ]}
          />
        </Flex>
      ),
    },
  ];

  return (
    <BrandedPageLayout
      title="Tin nhóm Zalo"
      subtitle="Nội dung và nhóm nhận tin khi hệ thống có việc cần nhân sự vào xử lý."
      breadcrumbItems={[{ title: "Cấu hình" }, { title: "Tin nhóm Zalo" }]}
    >
      <ListToolbar
        loading={list.loading}
        onReload={list.reload}
        actions={
          <Tooltip
            title={
              canAddMore ? "" : "Mọi sự kiện hệ thống hỗ trợ đều đã có cấu hình."
            }
          >
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              disabled={!canAddMore}
              onClick={() => setEditing({})}
            >
              Thêm mới
            </Button>
          </Tooltip>
        }
      />

      <CmsTableCard
        columns={columns}
        dataSource={list.loading ? [] : rows}
        loading={list.loading}
        pagination={false}
        locale={{
          emptyText: list.loading ? (
            <Skeleton active style={{ padding: 24 }} />
          ) : (
            <Empty description={list.error || "Chưa có cấu hình tin nhóm nào"} />
          ),
        }}
        bordered
        scroll={{ x: "max-content" }}
      />

      <ZaloGroupMessageForm
        open={Boolean(editing)}
        record={editing}
        events={eventOptions}
        usedCodes={usedCodes}
        onClose={() => setEditing(null)}
        onSaved={list.reload}
      />
    </BrandedPageLayout>
  );
}
